import React, { useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../shared/ui";
import { TransactionItem } from "../../types";
import { formatTransactionStatus } from "../../utils/statusLookup";

interface ReconcileModalProps {
  open: boolean;
  transaction: TransactionItem;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: { reason: string; adjustWallet: boolean }) => void;
}

export const ReconcileModal: React.FC<ReconcileModalProps> = ({
  open,
  transaction,
  isLoading,
  onOpenChange,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [adjustWallet, setAdjustWallet] = useState(true);

  const rawBusinessName = transaction.business?.name;
  const businessName =
    typeof rawBusinessName === "object" && rawBusinessName !== null
      ? (rawBusinessName as any).value || (rawBusinessName as any).name
      : rawBusinessName;

  const customerName =
    transaction.customer?.name ||
    businessName ||
    transaction.accountName ||
    "Customer";

  const currency = (transaction.currency || "USD").toUpperCase();
  const absAmount = Math.abs(Number(transaction.amount) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const handleClose = () => {
    if (isLoading) return;
    onOpenChange(false);
    setReason("");
  };

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    onConfirm({ reason: reason.trim(), adjustWallet });
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      contentClassName="bg-white rounded-2xl max-w-[480px]"
    >
      <ModalHeader className="items-center border-b border-[#F2F4F7] pb-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F4EBFF] text-[#6F00FF]">
            <RefreshCw size={16} />
          </div>
          <ModalTitle className="text-[18px] font-semibold text-[#101828]">
            Reconcile transaction
          </ModalTitle>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="rounded-lg p-1 text-[#667085] hover:bg-[#F9FAFB] hover:text-[#101828]"
        >
          <X size={18} />
        </button>
      </ModalHeader>

      <form onSubmit={handleConfirm}>
        <ModalBody className="flex flex-col gap-5 py-5">
          <div className="rounded-xl border border-[#EEF1FB] bg-[#F8F9FF] p-4 text-[13px]">
            <div className="flex justify-between py-1">
              <span className="text-[#667085]">Customer</span>
              <span className="font-medium text-[#101828]">{customerName}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[#667085]">Amount</span>
              <span className="font-semibold text-[#101828]">
                {currency} {absAmount}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[#667085]">Current status</span>
              <span className="font-medium text-[#B54708]">
                {formatTransactionStatus(transaction.status) || "Pending"}
              </span>
            </div>
          </div>

          <div className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#D0D5DD] p-3 text-[13px]">
            <input
              type="checkbox"
              checked={adjustWallet}
              onChange={(e) => setAdjustWallet(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded text-[#6F00FF] accent-[#6F00FF]"
            />
            <div className="flex flex-col">
              <span className="font-medium text-[#101828]">
                Reconcile & adjust customer wallet balance
              </span>
              <span className="text-[12px] text-[#667085]">
                Automatically credit or settle the funds to the customer&apos;s ledger.
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <p className="text-[13px] font-medium text-[#344054]">
              Reason / Remark <span className="font-normal italic text-[#667085]">• Optional</span>
            </p>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Bank confirmation received, manually reconciled ledger"
              className="w-full resize-none rounded-xl border border-[#D0D5DD] p-3 text-[13px] text-[#101828] placeholder-[#667085] shadow-sm focus:border-[#6F00FF] focus:outline-none"
            />
          </div>
        </ModalBody>

        <ModalFooter className="border-t border-[#F2F4F7] px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2.5 text-[13px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !reason.trim()}
            className="rounded-xl bg-[#6F00FF] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#5E01D6] disabled:bg-[#98A2B3]"
          >
            {isLoading ? "Reconciling..." : "Confirm reconciliation"}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
